import { Button } from "@freightsimple/ui-core/Controls/Button";
import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { FormItem } from "@freightsimple/ui-core/FormLayout/FormItem";
import { Heading } from "@freightsimple/ui-core/Typography/Heading";
import { Logo } from "@freightsimple/ui-core/Display/Logo";
import { PasswordInput } from "@freightsimple/ui-core/Controls/PasswordInput";
import { PhoneInput } from "@freightsimple/ui-core/Controls/PhoneInput";
import { ShowFormErrors } from "@freightsimple/ui-core/FormLayout/ShowFormErrors";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { TextInput } from "@freightsimple/ui-core/Controls/TextInput";
import { ResponseError } from "@freightsimple/generated-dashboard-openapi-client/runtime";
import { useEffect, useState } from "react";
import useQuery from "@/Hooks/useQuery";
import Animation from "@/Shared/Animations/Animation";
import { useInvitationsApi } from "@/Hooks/useInvitationsApi";
import { useAuthenticationAcceptInvitation } from "../Hooks/useAuthenticationAcceptInvitation";
import { useSignInDirectly } from "@/Shared/Authentication/Hooks/useAuthenticationSignIn";

// query-string parses each param as string | string[] | null; an invitation
// link always carries single values, so narrow to a plain string.
function asString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

export function AuthenticationAcceptInvitation() {
  const query = useQuery();
  const companyName = asString(query["companyName"]);
  const inviteeEmail = asString(query["inviteeEmail"]);
  const inviteeName = asString(query["inviteeName"]);
  const inviterName = asString(query["inviterName"]);
  const inviteToken = asString(query["inviteToken"]);

  const invitationsApi = useInvitationsApi();
  const { signIn, continueAfterReset } = useSignInDirectly();

  const [loading, setLoading] = useState(false);
  const [wasSuccess, setWasSuccess] = useState(false);
  const [forceValidation, setForceValidation] = useState(false);
  const [accessToken, setAccessToken] = useState("");
  const [expiresAt, setExpiresAt] = useState<number | undefined>();
  const [invitationInvalidReason, setInvitationInvalidReason] = useState<
    string | undefined
  >(undefined);

  const {
    acceptInvitation,
    errorMessages,
    email,
    setEmail,
    password,
    setPassword,
    name,
    setName,
    phoneNumber,
    setPhoneNumber,
    phoneNumberExtension,
    setPhoneNumberExtension,
    generalErrorMessage,
    isValid,
  } = useAuthenticationAcceptInvitation(inviteeEmail, inviteeName);

  useEffect(() => {
    async function validateInvitation() {
      if (!inviteToken) {
        return;
      }
      try {
        await invitationsApi.getInvitationByToken(
          { tokenId: inviteToken },
          { cache: "no-cache" },
        );
      } catch (e) {
        if (e instanceof ResponseError) {
          if (e.response.status === 410) {
            setInvitationInvalidReason("Expired");
          }
          if (e.response.status === 404) {
            setInvitationInvalidReason("Revoked");
          }
        }
      }
    }
    validateInvitation();
  }, [inviteToken]);

  async function onAcceptInvitation() {
    if (loading) {
      return;
    }
    setForceValidation(true);
    if (!isValid) {
      return;
    }

    setLoading(true);
    try {
      const success = await acceptInvitation(inviteToken);
      if (success) {
        const { accessToken: newAccessToken, expiresAt: newExpiresAt } =
          await signIn(email, password);
        setAccessToken(newAccessToken);
        setExpiresAt(newExpiresAt);
        setWasSuccess(true);
      }
    } catch (e) {
      console.error("Something went wrong accepting the invitation", { e });
    } finally {
      setLoading(false);
    }
  }

  if (invitationInvalidReason !== undefined) {
    return (
      <Stack gap={6}>
        <Logo />
        <Stack gap={4}>
          <Heading level={1} size="4xl" weight="semibold">
            Invitation {invitationInvalidReason}
          </Heading>
          <T size="sm" color="muted">
            Please ask for another invitation link.
          </T>
        </Stack>
        <Stack align="center">
          <Animation
            pathToAnimation="/animations/yellow-exclamation.json"
            lottieFilesUrl="https://lottiefiles.com/packages/lf20_HYlQBb"
            speed={0.5}
            width={300}
          />
        </Stack>
      </Stack>
    );
  }

  if (wasSuccess) {
    return (
      <Stack gap={6}>
        <Logo />
        <Stack gap={4}>
          <Heading level={1} size="4xl" weight="semibold">
            Account successfully created!
          </Heading>
          <T size="sm" color="secondary">
            You can now continue to use FreightSimple
          </T>
        </Stack>
        <Stack align="center">
          <Animation
            pathToAnimation="/animations/success-blue.json"
            lottieFilesUrl="https://lottiefiles.com/4431-success"
            loop
            speed={0.5}
            width={300}
          />
        </Stack>
        <Button
          primary
          fullWidth
          size="large"
          onClick={() => continueAfterReset(accessToken, expiresAt)}
          data-testid="accept-invitation-continue-button"
        >
          Continue
        </Button>
      </Stack>
    );
  }

  return (
    <ControlPresentationProvider presentation="underlined">
      {/* Pressing Enter submits; preventDefault stops the browser's native GET
          navigation and drives the same path as the button. */}
      <form
        onSubmit={(event) => {
          event.preventDefault();
          onAcceptInvitation();
        }}
        noValidate
      >
        <Stack gap={6}>
          <Stack gap={10}>
            <Logo />
            <Stack gap={1}>
              <Heading level={1} size="4xl" weight="semibold">
                Accept Invitation
              </Heading>
              <T size="sm" color="secondary">
                <strong>{inviterName}</strong> has invited you to join the
                company <strong>{companyName}</strong> using FreightSimple.
              </T>
              <T size="sm" color="secondary">
                Please complete your details below to join this organization.
              </T>
            </Stack>
          </Stack>

          <ShowFormErrors show={forceValidation}>
            <Stack gap={5}>
              <FormItem
                label="Email Address"
                fillWidth
                emphasizeLabel
                error={errorMessages.email}
              >
                <TextInput
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  name="accept_invitation_email"
                  width="full"
                />
              </FormItem>

              <FormItem
                label="Full Name"
                fillWidth
                emphasizeLabel
                error={errorMessages.name}
              >
                <TextInput
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoComplete="name"
                  name="accept_invitation_name"
                  width="full"
                />
              </FormItem>

              <FormItem
                label="Phone Number"
                fillWidth
                emphasizeLabel
                error={errorMessages.phoneNumber}
              >
                <PhoneInput
                  phoneNumber={phoneNumber}
                  extension={phoneNumberExtension}
                  onPhoneNumberChange={setPhoneNumber}
                  onExtensionChange={setPhoneNumberExtension}
                  width="full"
                />
              </FormItem>

              <FormItem
                label="Password"
                tooltip="Use at least 8 characters."
                fillWidth
                emphasizeLabel
                error={errorMessages.password}
              >
                <PasswordInput
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  name="accept_invitation_password"
                  width="full"
                  placeholder="Enter your new password"
                />
              </FormItem>
            </Stack>
          </ShowFormErrors>

          <Stack gap={4}>
            <Button
              primary
              fullWidth
              size="large"
              onClick={onAcceptInvitation}
              loading={loading}
              disabled={loading}
              disabledMessage="Accepting your invitation…"
              data-testid="accept-invitation-button"
            >
              Accept Invitation
            </Button>

            {generalErrorMessage && (
              <T color="red" align="center">
                {generalErrorMessage}
              </T>
            )}
          </Stack>
        </Stack>
      </form>
    </ControlPresentationProvider>
  );
}
